/* eslint-disable lingui/no-unlocalized-strings */
// Apply Edge — Help center. Port of design/source/public/help.html.
// Chrome (NavBar/Footer) is owned by HomeLayout — do not render here.
//
// AUP: copy from design uses literal restricted phrase; we swap to
// "Apply Assist" in user-facing text (same swap as commit 0cf0c3bf).
import { t } from "@lingui/macro";
import {
  CreditCardIcon,
  FileTextIcon,
  LightningIcon,
  MagnifyingGlassIcon,
  PlugIcon,
  RocketIcon,
  ShieldIcon,
  WrenchIcon,
} from "@phosphor-icons/react";
import type { ElementType } from "react";
import { useState } from "react";

import { SEO } from "@/client/components/seo";

type Category = {
  id: string;
  Icon: ElementType;
  title: string;
  blurb: string;
};

const CATEGORIES: readonly Category[] = [
  {
    id: "getting-started",
    Icon: RocketIcon,
    title: "Getting started",
    blurb: "Sign up, onboarding, first resume, first Apply Assist run.",
  },
  {
    id: "resumes",
    Icon: FileTextIcon,
    title: "Resumes & cover letters",
    blurb: "Templates, tailoring, ATS scoring, exports.",
  },
  {
    id: "apply-assist",
    Icon: LightningIcon,
    title: "Apply Assist",
    blurb: "Daily caps, screening Qs, kill-switches, reviewing the queue.",
  },
  {
    id: "jobs",
    Icon: MagnifyingGlassIcon,
    title: "Jobs feed & profiles",
    blurb: "Search profiles, deduping, why a job appeared, mute companies.",
  },
  {
    id: "billing",
    Icon: CreditCardIcon,
    title: "Billing & plans",
    blurb: "Pro vs Ultimate, upgrades, refunds, invoicing, taxes.",
  },
  {
    id: "privacy",
    Icon: ShieldIcon,
    title: "Privacy & security",
    blurb: "Data export, account deletion, what AI sees, 2FA, recruiters.",
  },
  {
    id: "integrations",
    Icon: PlugIcon,
    title: "Integrations",
    blurb: "LinkedIn, Google Drive, ATS systems, calendar, email forwarding.",
  },
  {
    id: "troubleshooting",
    Icon: WrenchIcon,
    title: "Troubleshooting",
    blurb: "Stuck applications, AI errors, login issues, browser quirks.",
  },
];

const TOP_FAQS = [
  {
    q: "How is Apply Edge different from Simplify or LinkedIn Easy Apply?",
    a: "We tailor a fresh resume per role, send the application end-to-end (not just a one-click), and let you review everything in a queue before it goes out. Simplify autofills forms in your browser; Easy Apply ships your generic resume to whoever's listening. We're closer to a job-search agent that you supervise.",
  },
  {
    q: "Will employers know my resume was AI-generated?",
    a: "Probably not — and even if they suspect it, in 2026 most recruiters assume some assistance. The bigger risk is sending a resume that's clearly mass-produced. Tailoring per role solves that. Pro tip: always read your AI draft and edit at least one bullet to your voice before submitting.",
  },
  {
    q: "Can Apply Assist submit fake or wrong information?",
    a: "Apply Assist only fills in fields you've answered in your job profile. Anything new (a screening question we haven't seen, a custom essay) routes to your review queue first. We never invent dates, salaries, or references.",
  },
  {
    q: "What happens if I cancel?",
    a: "You drop to Free at the end of the billing period. Your resumes and applications stay accessible (Free has lower caps for new items but doesn't delete history). You can re-upgrade any time and pick up where you left off.",
  },
  {
    q: "Does Apply Edge guarantee a job?",
    a: "No, and we'd be lying if we did. We've watched our top 10% of users land 4× more interviews than baseline by combining tailoring + steady volume + good profile data. Volume alone doesn't work; tailoring without volume doesn't work. Both, plus a good market fit, does.",
  },
] as const;

const ARTICLES: Record<string, readonly { t: string; read: string }[]> = {
  "getting-started": [
    { t: "Quick start: from sign-up to first Apply Assist run in 12 minutes", read: "8 min" },
    { t: "Importing your existing resume (PDF, DOCX, LinkedIn)", read: "4 min" },
    { t: "Setting up your first job profile", read: "5 min" },
    { t: "Understanding the daily review queue", read: "3 min" },
    { t: "Pause, kill-switch, and what 'safe mode' does", read: "2 min" },
    { t: "Mobile companion app — what it does, what it doesn't", read: "3 min" },
    { t: "Onboarding states and how to skip them", read: "2 min" },
    { t: "What happens during your first 7 days", read: "4 min" },
  ],
  resumes: [
    { t: "How AI tailoring actually works (under the hood)", read: "6 min" },
    { t: "ATS scoring — what the number means and what to ignore", read: "5 min" },
    { t: "Reviewing AI changes with diff view", read: "3 min" },
    { t: "Choosing a template that won't break ATS parsers", read: "4 min" },
    { t: "Exporting to PDF, DOCX, and Google Docs", read: "2 min" },
    { t: "Cover letters: when they help and when to skip", read: "5 min" },
    { t: "Locking sections so AI doesn't touch them", read: "3 min" },
    { t: "Multiple resumes for different career tracks", read: "4 min" },
    { t: "Adding skills the AI keeps missing", read: "3 min" },
    { t: "International resume formats (UK, Germany, Brazil)", read: "6 min" },
    { t: "Photo, portrait, headshot — when to include", read: "3 min" },
    { t: "Handling employment gaps in tailored bullets", read: "5 min" },
    { t: "Adding patents, papers, talks, or open-source", read: "4 min" },
  ],
  "apply-assist": [
    { t: "How daily caps work (and why they're conservative by default)", read: "4 min" },
    { t: "Reviewing the queue: what to approve vs. edit", read: "5 min" },
    { t: "Screening questions library and how to fill it", read: "6 min" },
    { t: "Kill-switch and pause — what happens to in-flight apps", read: "3 min" },
    { t: "What we won't send applications to (and why)", read: "4 min" },
    { t: "Custom rules: salary minimums, location filters, company size", read: "5 min" },
    { t: "Why an application showed up that doesn't match", read: "3 min" },
    { t: "How we handle 'optional' fields you haven't answered", read: "4 min" },
    { t: "Application replies — where they go, how to track", read: "3 min" },
    { t: "Recovering a rejected or failed submission", read: "3 min" },
    { t: "Apply Assist on Free vs Pro vs Ultimate", read: "2 min" },
  ],
  billing: [
    { t: "Comparing Free, Pro, and Ultimate", read: "4 min" },
    { t: "Switching between monthly and yearly", read: "2 min" },
    { t: "Refunds — when you qualify and how to request", read: "3 min" },
    { t: "Invoices, VAT, and tax exemption (B2B)", read: "4 min" },
    { t: "Failed payment — what happens to your account", read: "3 min" },
    { t: "Pausing your subscription instead of canceling", read: "2 min" },
    { t: "Student & job-seeker hardship discounts", read: "3 min" },
    { t: "Currency, exchange rates, and regional pricing", read: "3 min" },
    { t: "Annual plan: what counts as 'first 14 days'", read: "2 min" },
    { t: "Gifting a subscription", read: "2 min" },
  ],
};

const COMMON_SEARCHES = ["refund", "2FA", "kill switch", "ATS score", "recruiter contact"];

export const HelpPage = () => {
  const [query, setQuery] = useState("");
  const [openFaq, setOpenFaq] = useState<number>(0);

  return (
    <div className="min-h-screen delay-100 duration-500 animate-in fade-in slide-in-from-bottom-4 fill-mode-both">
      <SEO
        title={t`Help center — Apply Edge`}
        description={t`Browse help articles and guides for Apply Edge — getting started, resumes, applications, billing, security, and troubleshooting.`}
        canonical="/help"
      />

      {/* Search hero */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1280px] px-6 py-20">
          <span className="text-xs font-medium text-muted-foreground">Help center</span>
          <h1 className="mt-3 font-serif text-[44px] font-semibold leading-[1.04] tracking-tight text-foreground sm:text-[56px]">
            How can we <span className="italic">help</span>?
          </h1>
          <p className="mt-4 max-w-xl text-[15.5px] leading-[1.65] text-muted-foreground">
            Search help guides or browse by category. If you can&apos;t find what you need, email us
            and we&apos;ll help.
          </p>
          <div className="mt-8 max-w-2xl">
            <div className="flex h-12 items-center gap-3 rounded-lg border border-border bg-surface px-4 shadow-sm focus-within:ring-2 focus-within:ring-primary/30">
              <MagnifyingGlassIcon size={16} className="shrink-0 text-muted-foreground" />
              <input
                value={query}
                placeholder="Try 'apply assist daily cap' or 'export to PDF'"
                aria-label="Search help articles"
                className="w-full bg-transparent text-[14px] leading-[1.5] text-foreground placeholder:text-muted-foreground focus:outline-none"
                onChange={(e) => {
                  setQuery(e.target.value);
                }}
              />
              <kbd className="hidden shrink-0 text-xs font-medium text-muted-foreground sm:inline">
                ⌘ K
              </kbd>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-[12px] text-muted-foreground">
              <span>Common:</span>
              {COMMON_SEARCHES.map((tag) => (
                <button
                  key={tag}
                  className="rounded-full border border-border bg-background px-2.5 py-0.5 hover:text-foreground"
                  onClick={() => {
                    setQuery(tag);
                  }}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Category grid */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1280px] px-6 py-20">
          <h2 className="font-serif text-[28px] font-semibold tracking-tight text-foreground">
            Browse by category
          </h2>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {CATEGORIES.map((c) => (
              <a
                key={c.id}
                href={"#" + c.id}
                className="group rounded-2xl border border-border bg-surface p-5 shadow-sm transition-all duration-150 ease-out hover:-translate-y-[1px] hover:border-foreground/20 hover:shadow-md"
              >
                <div className="flex items-start justify-between">
                  <div className="grid h-9 w-9 place-items-center rounded-lg bg-primary/10 text-primary">
                    <c.Icon size={18} />
                  </div>
                </div>
                <div className="mt-4 text-[15.5px] font-medium text-foreground">{c.title}</div>
                <p className="mt-1.5 text-[13px] leading-[1.55] text-muted-foreground">{c.blurb}</p>
                <div className="mt-4 inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground opacity-0 transition group-hover:opacity-100">
                  Open <span aria-hidden>→</span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Top FAQ */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1280px] px-6 py-20">
          <div className="grid grid-cols-1 gap-12 md:grid-cols-[280px_1fr]">
            <div>
              <span className="text-xs font-medium text-muted-foreground">FAQ</span>
              <h2 className="mt-3 font-serif text-[28px] font-semibold leading-[1.1] tracking-tight text-foreground">
                The five questions <span className="italic">everyone</span> asks
              </h2>
              <p className="mt-3 text-[13.5px] leading-[1.65] text-muted-foreground">
                If yours isn&apos;t here, search above or write to{" "}
                <a className="text-primary hover:underline" href="mailto:hello@apply-edge.com">
                  hello@apply-edge.com
                </a>
                .
              </p>
            </div>
            <div className="divide-y divide-border rounded-2xl border border-border bg-surface shadow-sm transition-all duration-150 ease-out hover:-translate-y-[1px] hover:shadow-md">
              {TOP_FAQS.map((f, i) => {
                const open = openFaq === i;
                return (
                  <button
                    key={f.q}
                    aria-expanded={open}
                    className="flex w-full flex-col items-start gap-3 p-5 text-left"
                    onClick={() => {
                      setOpenFaq(open ? -1 : i);
                    }}
                  >
                    <div className="flex w-full items-center justify-between gap-4">
                      <div className="text-[15px] font-medium text-foreground">{f.q}</div>
                      <span
                        aria-hidden
                        className={
                          "font-mono text-[14px] text-muted-foreground transition " +
                          (open ? "rotate-45" : "")
                        }
                      >
                        +
                      </span>
                    </div>
                    {open && (
                      <div className="text-[13.5px] leading-[1.7] text-muted-foreground">{f.a}</div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Article sections */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1280px] px-6 py-20">
          <div className="space-y-16">
            {Object.entries(ARTICLES).map(([catId, articles]) => {
              const cat = CATEGORIES.find((c) => c.id === catId);
              if (!cat) return null;
              const filtered = query
                ? articles.filter((a) => a.t.toLowerCase().includes(query.toLowerCase()))
                : articles;
              if (filtered.length === 0) return null;
              return (
                <section key={catId} id={catId}>
                  <div className="flex items-end justify-between">
                    <div>
                      <div className="flex items-center gap-2.5">
                        <div className="grid h-7 w-7 place-items-center rounded-md bg-primary/10 text-primary">
                          <cat.Icon size={16} />
                        </div>
                        <h3 className="font-serif text-[22px] font-semibold tracking-tight text-foreground">
                          {cat.title}
                        </h3>
                      </div>
                      <p className="mt-1.5 text-[13px] text-muted-foreground">{cat.blurb}</p>
                    </div>
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid -- TODO: route to /help/category/[slug] once the category index ships */}
                    <a
                      href="#"
                      className="text-xs font-medium text-muted-foreground hover:text-foreground"
                    >
                      View category →
                    </a>
                  </div>
                  <div className="mt-5 grid grid-cols-1 gap-x-8 gap-y-1 sm:grid-cols-2">
                    {filtered.map((a) => (
                      // eslint-disable-next-line jsx-a11y/anchor-is-valid -- TODO: route to /help/article/[slug] once articles are linkable
                      <a
                        key={a.t}
                        href="#"
                        className="group flex items-center justify-between gap-4 border-b border-border py-3 transition hover:border-foreground/30"
                      >
                        <div className="text-[13.5px] text-foreground/85 group-hover:text-foreground">
                          {a.t}
                        </div>
                        <div className="font-mono text-[10.5px] text-muted-foreground">
                          {a.read}
                        </div>
                      </a>
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact strip */}
      <section>
        <div className="mx-auto max-w-[1280px] px-6 py-20">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="rounded-2xl border border-border bg-surface p-6 shadow-sm transition-all duration-150 ease-out hover:-translate-y-[1px] hover:shadow-md">
              <div className="text-xs font-medium text-muted-foreground">Email support</div>
              <div className="mt-3 font-serif text-[20px] font-semibold tracking-tight text-foreground">
                hello@apply-edge.com
              </div>
              <p className="mt-2 text-[12.5px] leading-[1.6] text-muted-foreground">
                We aim to reply within a few business days.
              </p>
            </div>
            <div className="rounded-2xl border border-border bg-surface p-6 shadow-sm transition-all duration-150 ease-out hover:-translate-y-[1px] hover:shadow-md">
              <div className="text-xs font-medium text-muted-foreground">Contact</div>
              <div className="mt-3 font-serif text-[20px] font-semibold tracking-tight text-foreground">
                Tell us what you need
              </div>
              <p className="mt-2 text-[12.5px] leading-[1.6] text-muted-foreground">
                Include your account email and any relevant page, invoice, or application details.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
